import { useDispatch, useSelector } from "react-redux";
import { showAuthModal } from "store/application/application-action";
import { useAddLikeMutation, useRemoveLikeMutation } from "queries";
import { useCallback, useState, useEffect } from "react";
import { IAppState } from "store/app-state";
import { useLikedGifs } from "hooks";
import { metrics } from "utils/metrics";
import { setAnonymousLikedToast, loadAnonymousLikedToast } from "utils/storage";
import { toast } from "react-hot-toast";

interface IUseLikeActionsProps {
  gifId: string;
  initialLikesCount: number;
  ref: any;
}

const STATUS_401 = 401;
const REMOVE_CLASS_DELAY = 500;

export const useLikeActions = ({ gifId, initialLikesCount, ref }: IUseLikeActionsProps) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: IAppState) => state.user.currentUser);
  const isUserLoggedIn = currentUser && !!Object.entries(currentUser).length;
  const likes = useLikedGifs();

  const [liked, setLiked] = useState<boolean>(false);
  const [likesCount, setLikesCount] = useState(initialLikesCount);

  const addLikeMutation = useAddLikeMutation();
  const removeLikeMutation = useRemoveLikeMutation();

  const showToast = useCallback(() => {
    if (!isUserLoggedIn && !loadAnonymousLikedToast()) {
      toast.success(
        "Please log in to make sure your liked gifs aren’t lost",
        { duration: 5000 }
      );
      setAnonymousLikedToast(true);
    }
  }, [isUserLoggedIn]);

  const handleUnauthorized = useCallback((e: any) => {
    if (e.response.status === STATUS_401) {
      dispatch(showAuthModal());
    }
  }, [dispatch]);

  const handleAddLike = useCallback(async () => {
    setLiked(true);
    setLikesCount((prev) => prev + 1);
    ref.current?.classList.add("isLiked");

    try {
      await addLikeMutation.mutateAsync({ gifId });
      await metrics.sendLiked({ gif: gifId });
      showToast();
      console.debug(`Gif ${gifId} liked.`);
    } catch (e: any) {
      console.error(`Gif ${gifId} could not be liked: ${e}.`);
      setLiked(false);
      setLikesCount((prev) => prev - 1);
      ref.current?.classList.remove("isLiked");
      handleUnauthorized(e);
    } finally {
      setTimeout(() => {
        ref.current?.classList.remove("isLiked");
      }, REMOVE_CLASS_DELAY);
    }
  }, [gifId, showToast, addLikeMutation, handleUnauthorized, ref]);

  const handleRemoveLike = useCallback(async () => {
    setLikesCount((prev) => prev - 1);
    setLiked(false);
    ref.current?.classList.add("isUnLiked");

    try {
      await removeLikeMutation.mutateAsync({ gifId });
      console.debug(`Gif ${gifId} unliked.`);
    } catch (e: any) {
      console.error(`Gif ${gifId} could not be unliked: ${e}.`);
      setLiked(true);
      setLikesCount((prev) => prev + 1);
      ref.current?.classList.remove("isUnLiked");
      handleUnauthorized(e);
    } finally {
      setTimeout(() => {
        ref.current?.classList.remove("isUnLiked");
      }, REMOVE_CLASS_DELAY);
    }
  }, [gifId, removeLikeMutation, handleUnauthorized, ref]);

  const handleLike = useCallback(() => {
    if (liked) {
      handleRemoveLike();
    } else {
      handleAddLike();
    }
  }, [liked, handleRemoveLike, handleAddLike]);

  useEffect(() => {
    setLiked(likes.indexOf(gifId) >= 0);
  }, [gifId, likes]);

  return {
    liked,
    likesCount,
    handleLike,
  };
};
